import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'
import * as shared from '../shared'

/** Modal for adding a new service center */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.button.Component,
    ds.input.text.Component,
    shared.functionalizations.Component,
  ],
  selector: 'atn-space-service-center-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.serviceCenters.Collection} */
  public readonly serviceCentersEc = ngCore.inject(
    state.space.full.serviceCenters.Collection,
  )

  /** The service center currently being created/edited */
  private readonly _serviceCenterSg = ngCore.computed(() =>
    this.serviceCentersEc.plugins.currentEntityPlugin.feature.selectors
      .getEditedEntity()
      .asSignal(),
  )

  /** Modal title based on whether creating or editing */
  public readonly titleSg = ngCore.computed(
    (
      isNew = this.serviceCentersEc.plugins.currentEntityPlugin.feature.selectors
        .isCreating()
        .asSignal(),
    ) => (isNew ? 'Add Service Center' : 'Edit Service Center'),
  )

  /** Editing service center name */
  public readonly nameSg = ngCore.computed(
    (sc = this._serviceCenterSg()) => sc?.name ?? '',
  )

  /** Editing service center functionalizations */
  public readonly functionalizationsSg = ngCore.computed(
    (sc = this._serviceCenterSg()) => sc?.functionalizations ?? [],
  )

  /** {@see {@link ents.extra.space.Functionalization.isFullyCoded}} */
  private readonly _isFullyCodedSg = ngCore.computed(
    (functionalizations = this.functionalizationsSg()) =>
      ents.extra.space.Functionalization.isFullyCoded(functionalizations),
  )

  /** Whether user can confirm/submit */
  public readonly canConfirmSg = ngCore.computed(
    (
      serviceCenterName = this.nameSg(),
      isFullyCoded = this._isFullyCodedSg(),
    ) => serviceCenterName.trim().length > 0 && isFullyCoded,
  )

  /** Update a functionalization */
  public updateFunctionalization({
    functionId,
    percent,
  }: ents.extra.space.Functionalization): void {
    this.serviceCentersEc.plugins.editingPlugin.feature.actions.edit
      .from('Space.ServiceCenters.Modal.FunctionalizationChanged')
      .dispatch({
        // TODO: Maybe we should extract this edit logic elsewhere
        edit: sc => {
          if (!sc) {
            return
          }
          sc.functionalizations ??= []
          const existing = sc.functionalizations.find(
            f => f.functionId === functionId,
          )
          if (existing) {
            existing.percent = percent
          } else {
            sc.functionalizations.push({ functionId, percent })
          }
        },
      })
  }

  /** Update service center name */
  public updateName(value: string | undefined): void {
    this.serviceCentersEc.plugins.editingPlugin.feature.actions.edit
      .from('Space.ServiceCenters.AddModal.NameChanged')
      .dispatch({
        edit: sc => {
          if (!sc) {
            return
          }
          sc.name = value ?? ''
        },
      })
  }

  /** Cancel dialog */
  public cancelClickHandler(): void {
    this.serviceCentersEc.plugins.editingPlugin.feature.actions.discard
      .from('Space.ServiceCenters.AddModal.Cancel.Clicked')
      .dispatch({ current: true })
  }

  /** Save new service center */
  public saveClickHandler(): void {
    this.serviceCentersEc.plugins.editingPlugin.feature.actions.submit
      .from('Space.ServiceCenters.AddModal.Save.Clicked')
      .dispatch({ current: true })
  }
}
